import React, { useCallback, useContext } from 'react';
import { useTranslation } from '@lib/useTypedTranslation';

import { DeviceInfoContext } from '../../../device';
import { getDeviceDetails } from '../../../../../services/core/devices';
import { OverviewDashboard, IOverviewItem } from '../../../../../components/overview/overview';
import { formatValue, formatFirstCapitalValue } from '../../../../../components/data-table/dataFormatters';
import Spinner from '../../../../../components/loading/loadingSpinner';
import { useWorldRequest } from '../../../../../lib/useWorldRequest';

interface IProps {
  deviceId: string
}

// Platform type, manufacturer and model come from DeviceInfoContext (already fetched by
// the examination page shell for the top chart/section), so only OS build version and the
// platform-specific firmware/BIOS version need their own (slower) getDeviceDetails fetch here -
// which also means the row layout is known immediately instead of shifting once that resolves.
export function DeviceAnomalyDeviceInfo(props: IProps) {
  const { deviceId } = props;
  const { platformType, manufacturer, model } = useContext(DeviceInfoContext);
  const { t } = useTranslation(['anomalyDetectionBeta', 'translation']);

  const deviceDetailsFetcher = useCallback(() => getDeviceDetails(deviceId), [deviceId]);
  const { data: device, loading } = useWorldRequest(deviceDetailsFetcher, { initialLoading: true });

  const formatIfLoaded = (value: string) => loading ? <Spinner text="" /> : formatValue(value);

  const items: IOverviewItem[] = [
    { label: t('PLATFORM', { ns: 'anomalyDetectionBeta' }), value: formatFirstCapitalValue(platformType) },
    { label: t('BUILD_VERSION', { ns: 'translation' }), value: formatIfLoaded(device?.operatingSystem?.buildVersion) },
    { label: t('MANUFACTURER', { ns: 'translation' }), value: formatValue(manufacturer) },
    { label: t('MODEL', { ns: 'translation' }), value: formatValue(model) }
  ];

  if (platformType === 'android') {
    items.push({ label: t('FIRMWARE_VERSION', { ns: 'translation' }), value: formatIfLoaded(device?.firmware?.version) });
  }
  if (platformType === 'windows') {
    items.push({ label: t('BIOS_VERSION', { ns: 'translation' }), value: formatIfLoaded(device?.bios?.version) });
  }

  return (
    <div className="device-anomaly-examination-page__device-info-card" data-id="device-anomaly-device-info">
      <div className="device-anomaly-examination-page__device-info-title">{t('DEVICE_INFORMATION_TITLE', { ns: 'anomalyDetectionBeta' })}</div>
      <div className="device-anomaly-examination-page__device-info-fixture"><OverviewDashboard items={items} /></div>
    </div>
  );
}
